import { api } from '@/lib/axios';
import type { DashboardFilters } from '../types/dashboard.types';

// Dashboard export endpoints
export type DashboardExportEndpoint = 
  | 'by-categories'
  | 'by-types' 
  | 'by-segments'
  | 'by-regions'
  | 'by-localities';

// Helper function to build query params for export (same as regular API)
const buildExportQueryParams = (filters?: DashboardFilters): string => {
  const params = new URLSearchParams();
  
  // Always filter by active status (statusId = 1) by default
  const statusIds = filters?.status?.length ? filters.status : [1];
  params.set('status', statusIds.join(','));
  
  if (filters?.segment?.length) {
    params.set('segment', filters.segment.join(','));
  }
  
  if (filters?.region?.length) {
    params.set('region', filters.region.join(','));
  }

  if (filters?.source) {
    params.set('source', filters.source);
  }
  
  return `?${params.toString()}`;
};

// Export function for dashboard statistics
export const exportDashboardToExcel = async (
  endpoint: DashboardExportEndpoint, 
  filters?: DashboardFilters
) => {
  const queryParams = buildExportQueryParams(filters);
  const url = `/api/stats/export/${endpoint}${queryParams}`;
  
  return await api.get(url, {
    responseType: 'blob',
  });
};

// Helper function to extract filename from Content-Disposition header
export const extractFilenameFromResponse = (
  response: { headers?: Record<string, string> }, 
  endpoint: DashboardExportEndpoint
): string => {
  const contentDisposition = response.headers?.['content-disposition'];
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, '');
    }
  }
  
  // Fallback filename based on endpoint
  const endpointNames: Record<DashboardExportEndpoint, string> = {
    'by-categories': 'estadisticas-categorias.xlsx',
    'by-types': 'estadisticas-tipos.xlsx',
    'by-segments': 'estadisticas-segmentos.xlsx',
    'by-regions': 'estadisticas-regiones.xlsx',
    'by-localities': 'estadisticas-localidades.xlsx',
  };
  
  return endpointNames[endpoint];
};

// Helper function to download blob as file
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};